import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, X, Sparkle, Copy, PaperPlaneTilt } from "@phosphor-icons/react";
import SendLeaseModal from "@/components/SendLeaseModal";

const STAGES = [
  { key: "pending", label: "Pending", color: "bg-slate-200 text-slate-900", head: "bg-slate-700" },
  { key: "offer_sent", label: "Offer Sent", color: "bg-amber-200 text-amber-900", head: "bg-amber-600" },
  { key: "accepted", label: "Accepted", color: "bg-emerald-200 text-emerald-900", head: "bg-emerald-600" },
  { key: "declined", label: "Declined", color: "bg-red-200 text-red-900", head: "bg-red-600" },
  { key: "renewed", label: "Renewed", color: "bg-cyan-200 text-cyan-900", head: "bg-cyan-600" },
  { key: "expired", label: "Expired", color: "bg-slate-400 text-white", head: "bg-slate-500" },
];

export default function LeaseRenewals() {
  const [leases, setLeases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [sending, setSending] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [r, e] = await Promise.all([
        api.get("/lease-renewals"),
        api.get("/lease-renewals/expiring-soon?days=90"),
      ]);
      setLeases(r.data || []);
      setExpiringSoon(e.data);
    } catch { toast.error("Failed to load leases"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const moveStage = async (l, stage) => {
    try {
      await api.patch(`/lease-renewals/${l.id}`, { stage });
      toast.success(`Moved to ${stage.replace("_", " ")}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const money = (c) => c ? `$${(c/100).toLocaleString()}` : "—";

  const buckets = Object.fromEntries(STAGES.map(s => [s.key, []]));
  leases.forEach(l => { (buckets[l.stage] || buckets.pending).push(l); });

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="lease-renewals-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <FileText weight="fill" className="text-cyan-500"/> Lease Renewal Pipeline
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Track every lease from 90 days before end-of-term through signed renewal. AI drafts each renewal offer letter.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-lease-btn"
            className="h-11 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> Add Lease
          </button>
        </div>

        {expiringSoon && expiringSoon.count > 0 && (
          <div className="rugged-card p-4 border-l-8 border-amber-500" data-testid="expiring-banner">
            <div className="flex items-center gap-2">
              <div className="text-3xl font-black text-amber-700">{expiringSoon.count}</div>
              <div>
                <div className="font-black uppercase tracking-widest text-sm">Leases expiring within 90 days</div>
                <div className="text-[10px] text-slate-600">Send renewal offers now — Philly requires 90 days notice on rent hikes {`>`} 5%.</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2 overflow-x-auto pb-2" data-testid="lease-kanban">
          {STAGES.map(s => (
            <div key={s.key} className="min-w-[220px]">
              <div className={`px-3 py-2 ${s.head} text-white font-black uppercase tracking-widest text-[10px] flex items-center justify-between`}>
                <span>{s.label}</span>
                <span className="bg-white/20 px-1.5 rounded text-[10px]">{buckets[s.key].length}</span>
              </div>
              <div className="border-2 border-t-0 border-slate-300 bg-slate-50 p-2 space-y-2 min-h-[300px]">
                {buckets[s.key].map(l => (
                  <div key={l.id} className="bg-white p-2 border-2 border-slate-200 shadow-[2px_2px_0_#e2e8f0] space-y-1"
                    data-testid={`lease-card-${l.id}`}>
                    <div className="font-black text-sm truncate">{l.tenant_name}</div>
                    <div className="text-[10px] text-slate-600 truncate">{l.property_address} {l.unit && `· ${l.unit}`}</div>
                    <div className="flex items-center justify-between text-[10px] pt-1 border-t border-slate-100">
                      <span>{money(l.current_rent_cents)}{l.proposed_rent_cents > 0 && ` → ${money(l.proposed_rent_cents)}`}</span>
                      {l.days_until_end !== null && (
                        <span className={l.days_until_end < 30 ? "text-red-600 font-bold" : "text-slate-600"}>
                          {l.days_until_end}d
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 pt-1">
                      <button onClick={() => setDrafting(l)} data-testid={`draft-offer-${l.id}`}
                        className="h-7 px-2 bg-cyan-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
                        <Sparkle weight="fill" size={9}/> Draft
                      </button>
                      <button onClick={() => setSending(l)} data-testid={`send-lease-${l.id}`}
                        className="h-7 px-2 bg-gradient-to-r from-indigo-600 to-violet-700 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
                        <PaperPlaneTilt weight="fill" size={9}/> Send Lease
                      </button>
                      <select value="" onChange={e => e.target.value && moveStage(l, e.target.value)}
                        data-testid={`stage-select-${l.id}`}
                        className="h-7 px-1 border-2 border-slate-300 text-[10px] font-black uppercase">
                        <option value="">→ Move</option>
                        {STAGES.filter(x => x.key !== l.stage).map(x => (
                          <option key={x.key} value={x.key}>{x.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
                {buckets[s.key].length === 0 && (
                  <div className="text-[10px] text-slate-400 text-center py-3">Empty</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {showNew && <LeaseModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
        {drafting && <DraftModal lease={drafting} onClose={() => setDrafting(null)} onSent={() => { setDrafting(null); load(); }}/>}
        {sending && <SendLeaseModal lease={sending} onClose={() => setSending(null)} onSent={() => { setSending(null); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function LeaseModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    tenant_name: "", tenant_email: "", property_address: "", unit: "",
    current_rent_cents: 0, proposed_rent_cents: 0, lease_end: "", stage: "pending", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/lease-renewals", {
        ...form,
        current_rent_cents: Math.round((parseFloat(form.current_rent_cents) || 0) * 100),
        proposed_rent_cents: Math.round((parseFloat(form.proposed_rent_cents) || 0) * 100),
      });
      toast.success("Lease added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="new-lease-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] max-h-[90vh] overflow-y-auto">
        <div className="p-3 bg-cyan-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Add Lease</div>
          <button onClick={onClose} data-testid="new-lease-close" className="p-1 hover:bg-cyan-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <input value={form.tenant_name} onChange={e => setForm({...form, tenant_name: e.target.value})} placeholder="Tenant name" data-testid="lease-tenant-name"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={form.tenant_email} onChange={e => setForm({...form, tenant_email: e.target.value})} placeholder="Tenant email"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input list="known-addresses" autoComplete="off" value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})} placeholder="Property address" data-testid="lease-address"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="Unit"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={form.current_rent_cents} onChange={e => setForm({...form, current_rent_cents: e.target.value})} placeholder="Current rent ($)"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input type="number" value={form.proposed_rent_cents} onChange={e => setForm({...form, proposed_rent_cents: e.target.value})} placeholder="Proposed rent ($)"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <input value={form.lease_end} onChange={e => setForm({...form, lease_end: e.target.value})} placeholder="Lease end (YYYY-MM-DD)" data-testid="lease-end"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !form.tenant_name || !form.property_address} data-testid="lease-save-btn"
            className="w-full h-11 bg-cyan-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Add Lease"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DraftModal({ lease, onClose, onSent }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const draft = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/lease-renewals/${lease.id}/draft-offer`, {}, { timeout: 60000 });
      setResult(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Draft failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { draft(); /* eslint-disable-line */ }, []);

  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(`Subject: ${result.subject}\n\n${result.body}`);
    toast.success("Copied to clipboard");
  };
  const markSent = async () => {
    try {
      await api.patch(`/lease-renewals/${lease.id}`, { stage: "offer_sent" });
      toast.success("Marked as Offer Sent");
      onSent();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="draft-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-cyan-600 text-white flex items-start justify-between sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">AI Renewal Offer</div>
            <div className="font-black uppercase tracking-widest">{lease.tenant_name}</div>
          </div>
          <button onClick={onClose} data-testid="draft-modal-close" className="p-1 hover:bg-cyan-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          {busy && <div className="text-sm text-slate-600 py-4 text-center">Claude drafting…</div>}
          {result && (
            <div className="p-3 border-4 border-slate-900 bg-slate-50 space-y-2" data-testid="draft-result">
              <div className="font-black text-sm">{result.subject}</div>
              <pre className="text-xs whitespace-pre-wrap font-mono max-h-96 overflow-y-auto">{result.body}</pre>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={copy} data-testid="draft-copy"
                  className="h-10 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-1">
                  <Copy size={12}/> Copy
                </button>
                <button onClick={markSent} data-testid="draft-mark-sent"
                  className="h-10 bg-emerald-600 text-white font-black uppercase tracking-widest text-xs shadow-[3px_3px_0_#0f172a]">
                  Mark Offer Sent
                </button>
              </div>
            </div>
          )}
          {!busy && !result && (
            <button onClick={draft} data-testid="draft-retry"
              className="w-full h-11 bg-cyan-600 text-white font-black uppercase tracking-widest text-sm shadow-[3px_3px_0_#0f172a]">
              Retry Draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
